import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useI18n } from "../../i18n";
import { getOrganizationCost, OrganizationCost } from "../../services/Cost";

/** This month and the three before it, newest first (YYYY-MM, UTC like the backend) */
const recentMonths = (count = 4) => {
  const now = new Date();
  return Array.from({ length: count }, (_, i) =>
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1)).toISOString().slice(0, 7)
  );
};

/** Ceiling bar colour: on track, close to the ceiling, at or over it */
const levelOf = (percent: number | null) =>
  percent == null ? "none" : percent >= 100 ? "over" : percent >= 80 ? "close" : "ok";

const BAR: Record<string, string> = {
  ok: "bg-brand",
  close: "bg-amber-500",
  over: "bg-red-600",
  none: "bg-brand",
};

/** An organization's admins follow what Queri costs against the agreed monthly ceiling. */
const CostPage: React.FC = () => {
  const { t, locale } = useI18n();
  const c = t.cost;
  const months = useMemo(() => recentMonths(), []);
  const [month, setMonth] = useState(months[0]);
  const [data, setData] = useState<OrganizationCost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"forbidden" | "failed" | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getOrganizationCost(month)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setData(res.data);
          setError(null);
        } else {
          setError(res.status === 403 ? "forbidden" : "failed");
        }
      })
      .catch(() => !cancelled && setError("failed"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [month]);

  const tag = locale === "sv" ? "sv-SE" : "en-GB";
  const sek = (n: number) =>
    new Intl.NumberFormat(tag, { style: "currency", currency: "SEK", maximumFractionDigits: n < 100 ? 2 : 0 }).format(n);
  const monthName = (m: string) =>
    new Date(`${m}-01T00:00:00Z`).toLocaleDateString(tag, { month: "long", year: "numeric", timeZone: "UTC" });

  if (loading && !data) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error === "forbidden" || (!data && error)) {
    return (
      <div className="w-full p-8">
        <h1 className="text-3xl font-bold tracking-tight">{c.title}</h1>
        <p className="mt-2 text-muted-foreground">{error === "forbidden" ? c.noAccess : c.loadFailed}</p>
      </div>
    );
  }
  if (!data) return null;

  const level = levelOf(data.percentOfCeiling);
  const isCurrent = month === months[0];
  const projectedOver =
    isCurrent && data.ceilingSek != null && data.projectedSek != null && data.projectedSek > data.ceilingSek;

  // Every day of the month gets a bar, also days without usage
  const [y, mo] = month.split("-").map(Number);
  const daysInMonth = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  const byDate = new Map(data.daily.map((d) => [d.date, d.costSek]));
  const days = Array.from({ length: daysInMonth }, (_, i) => {
    const date = `${month}-${String(i + 1).padStart(2, "0")}`;
    return { date, day: i + 1, costSek: byDate.get(date) ?? 0 };
  });
  const maxDay = Math.max(...days.map((d) => d.costSek), 0);
  const share = (n: number) => (data.spentSek > 0 ? `${Math.round((n / data.spentSek) * 100)}%` : "–");

  return (
    <div className="w-full space-y-6 p-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{c.title}</h1>
          <p className="text-muted-foreground">{c.subtitle(data.organizationName)}</p>
        </div>
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-[200px]" aria-label={c.month}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m} value={m}>
                  <span className="capitalize">{monthName(m)}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="p-6">
        <div className="text-sm text-muted-foreground">{c.spent}</div>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-3">
          <span className="text-4xl font-bold tabular-nums">{sek(data.spentSek)}</span>
          <span className="text-muted-foreground">
            {data.ceilingSek != null ? c.of(sek(data.ceilingSek)) : c.noCeiling}
          </span>
        </div>
        {data.ceilingSek != null && data.percentOfCeiling != null && (
          <>
            <div
              className="mt-4 h-3 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.min(100, Math.round(data.percentOfCeiling))}
            >
              <div
                className={`h-full rounded-full ${BAR[level]}`}
                style={{ width: `${Math.min(100, data.percentOfCeiling)}%` }}
              />
            </div>
            <div className="mt-2 flex flex-wrap justify-between gap-2 text-sm">
              <span className={level === "ok" ? "text-muted-foreground" : "font-medium text-amber-800 dark:text-amber-300"}>
                {c.percent(data.percentOfCeiling.toLocaleString(tag, { maximumFractionDigits: 1 }))}
              </span>
              {isCurrent && data.projectedSek != null && (
                <span className="text-muted-foreground">{c.projected(sek(data.projectedSek))}</span>
              )}
            </div>
            {(level === "over" || projectedOver) && (
              <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{level === "over" ? c.reached : c.overCeilingSoon}</span>
              </div>
            )}
            <p className="mt-3 text-xs text-muted-foreground">{c.alertNote}</p>
          </>
        )}
      </Card>

      <Card className="p-6">
        <h2 className="mb-4 text-base font-semibold">{c.daily}</h2>
        {data.spentSek === 0 ? (
          <p className="text-sm text-muted-foreground">{c.nothingYet}</p>
        ) : (
          <div className="flex h-40 items-end gap-[2px]" role="list" aria-label={c.daily}>
            {days.map((d) => (
              <div
                key={d.date}
                role="listitem"
                className="group relative flex h-full flex-1 items-end"
                title={`${new Date(`${d.date}T00:00:00Z`).toLocaleDateString(tag, { day: "numeric", month: "short", timeZone: "UTC" })}: ${sek(d.costSek)}`}
              >
                <div
                  className="w-full rounded-t-[4px] bg-brand/80 group-hover:bg-brand"
                  style={{ height: maxDay > 0 && d.costSek > 0 ? `${Math.max(2, (d.costSek / maxDay) * 100)}%` : "0" }}
                />
              </div>
            ))}
          </div>
        )}
        {data.spentSek > 0 && (
          <div className="mt-1 flex justify-between text-xs text-muted-foreground">
            <span>1</span>
            <span>{daysInMonth}</span>
          </div>
        )}
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="overflow-hidden">
          <div className="border-b p-4">
            <h2 className="text-base font-semibold">{c.byActivity}</h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{c.activity}</TableHead>
                <TableHead className="text-right">{c.amount}</TableHead>
                <TableHead className="w-20 text-right">{c.share}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.byCategory.map((row) => (
                <TableRow key={row.category}>
                  <TableCell>{c.categories[row.category] ?? c.categories.other}</TableCell>
                  <TableCell className="text-right tabular-nums">{sek(row.costSek)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{share(row.costSek)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Card className="overflow-hidden">
          <div className="border-b p-4">
            <h2 className="text-base font-semibold">{c.byPerson}</h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{c.person}</TableHead>
                <TableHead className="text-right">{c.amount}</TableHead>
                <TableHead className="w-20 text-right">{c.share}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.byUser.map((row) => (
                <TableRow key={row.userId ?? "none"}>
                  <TableCell>
                    <div className="font-medium">{row.name || row.email || c.unknownPerson}</div>
                    {row.name && row.email && <div className="text-xs text-muted-foreground">{row.email}</div>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{sek(row.costSek)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{share(row.costSek)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {data.previousMonths.length > 0 && (
        <Card className="p-4">
          <h2 className="mb-2 text-base font-semibold">{c.previous}</h2>
          <div className="flex flex-wrap gap-6 text-sm">
            {data.previousMonths.map((p) => (
              <button key={p.month} type="button" className="text-left hover:underline" onClick={() => setMonth(p.month)}>
                <div className="capitalize text-muted-foreground">{monthName(p.month)}</div>
                <div className="font-medium tabular-nums">{sek(p.costSek)}</div>
              </button>
            ))}
          </div>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        {c.rateNote(data.sekPerUsd.toLocaleString(tag, { maximumFractionDigits: 2 }))}
      </p>
    </div>
  );
};

export default CostPage;
